import { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Badge, Text, tokens, Radio, Stack, Link } from '@hotjar/hotsauce';
import type { Allowance, EnhancedPlan, Sku } from '@hotjar/product-catalog';
import { Trans, useTranslation } from '@hotjar/translations';
import { Pricing } from './Pricing';
import { AllowancesDropdown } from '../allowances-dropdown/AllowancesDropdown';
import { CONTACT_SALES } from '../../../constants';

const S = {
  Card: styled.label<{ isDisabled: boolean; isSelected: boolean }>`
    min-height: 140px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: min-content;
    column-gap: ${tokens.spacer.x3};
    background-color: ${tokens.ALIAS_COLORS.ui_background};
    border: 1px solid ${tokens.ALIAS_COLORS.ui_border};
    border-radius: ${tokens.radius.m};
    padding: ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x2pt5} ${tokens.spacer.x2};
    text-transform: none;
    color: ${(props) =>
      props.isDisabled
        ? tokens.ALIAS_TEXT_COLORS.text_disabled
        : tokens.ALIAS_TEXT_COLORS.text_default};
    ${(props) =>
      props.isSelected &&
      css`
        border-color: ${tokens.ALIAS_COLORS.ui_border_active};
        ${tokens.elevation(4)};
      `};
  `,
  FirstRow: styled.div<{ twoItems: boolean }>`
    grid-column: 1 / 3;
    display: flex;
    justify-content: ${(props) => (props.twoItems ? 'space-between' : 'flex-end')};
    align-items: center;
  `,
  Plan: styled.span`
    ${tokens.text('header5')};
    text-transform: capitalize;
  `,
  PriceContainer: styled.div`
    display: flex;
    justify-content: flex-end;
  `,
  ContactUs: styled(Text)`
    align-self: flex-end;
  `,
  AllowanceContainer: styled.div`
    grid-column: 1 / 3;
    margin-top: ${tokens.spacer.x1};
  `,
};

interface Props {
  plan: EnhancedPlan;
  billingPeriod: 'MONTHLY' | 'ANNUALLY';
  currency: 'EUR' | 'USD';
  isSelected: boolean;
  onSelect: (data: Sku) => void;
  current: {
    isCurrentPlan: boolean;
    allowance: Allowance;
  };
}

export function PlanRadioCard({
  plan,
  isSelected,
  billingPeriod,
  currency,
  current,
  onSelect,
}: Props): JSX.Element {
  const { t: tProductInformation } = useTranslation('checkout', {
    keyPrefix: 'product-information',
  });
  const { t: tSubscriptions } = useTranslation('checkout', { keyPrefix: 'wizard.subscriptions' });

  const [selectedAllowance, setSelectedAllowance] = useState<Allowance>(
    current.isCurrentPlan ? current.allowance : plan.allowances[0],
  );

  const isCurrentPlan: boolean = selectedAllowance.sku === current.allowance.sku;

  const select = (allowance?: Allowance) => {
    onSelect((allowance ?? selectedAllowance).sku);
  };

  const onDropdownChange = (allowance: Allowance) => {
    setSelectedAllowance(allowance);
    select(allowance);
  };

  const getSubtitleText = () => {
    if (plan.name === 'scale') {
      return tProductInformation('allowances.unlimited-sessions');
    }
    if ('monthlyResponses' in plan.allowances[0]) {
      return tProductInformation('allowances.monthly-responses', {
        allowances: plan.allowances[0].monthlyResponses,
      });
    }
    return tProductInformation('allowances.daily-sessions', {
      allowances: plan.allowances[0].dailyCcSessions,
    });
  };

  return (
    <S.Card isDisabled={isCurrentPlan} isSelected={isSelected}>
      <S.FirstRow twoItems={isCurrentPlan}>
        {isCurrentPlan && (
          <Badge
            kind="custom"
            size="s"
            color={tokens.color.ui_disabled}
            textColor={tokens.color.text_light}
          >
            {tSubscriptions('current-plan')}
          </Badge>
        )}
        <Radio
          value={plan.id}
          isDisabled={isCurrentPlan}
          isChecked={isSelected}
          onChange={() => select()}
          labelHtmlProps={{
            'aria-labelledby': `plan-${plan.id} price-${plan.id}`,
          }}
        />
      </S.FirstRow>

      <Stack id={`plan-${plan.id}`} direction="column" spacing="x0" aria-hidden>
        <S.Plan>{tProductInformation(`plan.${plan.name}`)}</S.Plan>
        {plan.allowances.length === 1 && <Text textColor="inherited">{getSubtitleText()}</Text>}
      </Stack>

      <S.PriceContainer id={`price-${plan.id}`} aria-hidden>
        {selectedAllowance.selfServeEnabled ? (
          <Pricing
            plan={plan.name}
            currency={currency}
            price={selectedAllowance.price}
            billingPeriod={billingPeriod}
          />
        ) : (
          <S.ContactUs textType="subtitle2" textColor="inherited">
            {tSubscriptions('book-a-call')}
          </S.ContactUs>
        )}
      </S.PriceContainer>

      {plan.allowances.length !== 1 && (
        <S.AllowanceContainer>
          <AllowancesDropdown
            allowances={plan.allowances}
            selectedAllowance={selectedAllowance}
            onChange={onDropdownChange}
            currency={currency}
            billingPeriod={billingPeriod}
            message={
              plan.name === 'scale' ? (
                <Trans
                  i18nKey="checkout:product-information.allowances.scale-book-a-call"
                  components={[
                    <Link
                      as="a"
                      kind="standalone"
                      href={CONTACT_SALES}
                      css={css`
                        ${tokens.getTextStyleProperties('caption')}
                        line-height: 1;
                      `}
                    >
                      {tProductInformation('allowances.scale-book-a-call')}
                    </Link>,
                  ]}
                />
              ) : undefined
            }
          />
        </S.AllowanceContainer>
      )}
    </S.Card>
  );
}
